import * as XLSX from 'xlsx';

import { IProject } from "./types/IProject";
import { IStructure } from "./types/IStructure";
import { IArea } from './types/IArea';
import { AreaGridUtils } from './util/AreaGridUtils';
import { IOrganixGridPoint } from './types/IOrganixGridPoint';
import { Filename } from './services/Filename';

/**
 * Exports a calculated area grid to Excel.
 */
class GridExporterExcel {

  static createMetadataWorksheet = (project: IProject, area: IArea, structures: IStructure[]) => {
    const width_meters = AreaGridUtils.width(area.rect);
    const height_meters = AreaGridUtils.height(area.rect);
    const horizontal_resolution = area.resolution;
    const vertical_resolution = AreaGridUtils.verticalResolution(area.rect, area.resolution);
    
    const worksheet = XLSX.utils.sheet_new();
    XLSX.utils.sheet_add_aoa(worksheet, [
      [ "Farm name", project.name ],
      [ "Species", "(Not set)" ],
      [ "Matrix dimension X", horizontal_resolution, "cells" ],
      [ "Matrix dimension Y", vertical_resolution, "cells" ],
      [ "Cell width", width_meters / horizontal_resolution, "m" ],
      [ "Cell height", height_meters / vertical_resolution, "m" ],
      [ "Number of structures", structures.length ],
      [ "Structure depth", area.structureDepth, "m" ],
      [ "Stocking", project.stocking, "animals" ],
    ]);
    worksheet["!cols"] = [ { wch: 25 }, { wch: 16 } ];    
    return worksheet;
  }

  static createStructuresWorksheet = (structures: IStructure[]) => {
    const worksheet = XLSX.utils.sheet_new();
    XLSX.utils.sheet_add_aoa(worksheet, [ ["ID" , "Name", "Stocking"], ...structures.map((s, idx) => [ idx+1, s.name, s.stocking ])]);
    worksheet["!cols"] = [ { wch: 10 }, { wch: 25 }, { wch: 10 } ];    
    return worksheet;
  }

  static createDataWorksheet = (data: IOrganixGridPoint[]) => {
    const header = 
      ["X" , "Y", "Latitude", "Longitude", "Depth", 
       "Current U", "Current V", "Structure", "Stocking"];

    const rows = data.map((p: IOrganixGridPoint) => [
      p.x,
      p.y,
      p.latitude,
      p.longitude,
      p.depth,
      p.u,
      p.v,
      p.structureIndex,
      p.stocking
    ]);

    const worksheet = XLSX.utils.sheet_new();
    XLSX.utils.sheet_add_aoa(worksheet, [ header, ...rows ]);
    return worksheet;
  }    
  
  static run = (project: IProject, area: IArea, structures: IStructure[], data: IOrganixGridPoint[]) => {
    const metadataWorksheet = this.createMetadataWorksheet(project, area, structures);
    const dataWorksheet = this.createDataWorksheet(data);
    const structuresWorksheet = this.createStructuresWorksheet(structures);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, metadataWorksheet, "Metadata");
    XLSX.utils.book_append_sheet(workbook, dataWorksheet, "Data");
    XLSX.utils.book_append_sheet(workbook, structuresWorksheet, "Structures");

    const filename = Filename.sanitize(`${project.name ?? "unnamed farm"}-${area.resolution}x${AreaGridUtils.verticalResolution(area.rect, area.resolution)}`);
    XLSX.writeFile(workbook, `${filename}.xlsx`, { compression: true });    
  }
}

export { GridExporterExcel }
